import {forwardRef, type ReactNode} from 'react';
import {Maximize2, Minimize2, PhoneOff} from 'lucide-react';

export type ControlTone = 'default' | 'pressed' | 'brand' | 'error' | 'warning' | 'unavailable';

interface ControlButtonProps {
    readonly icon: ReactNode;
    readonly label: string;
    readonly caption?: string;
    readonly tone?: ControlTone;
    readonly badge?: number;
    readonly alert?: boolean;
    readonly disabled?: boolean;
    readonly pressed?: boolean;
    readonly expanded?: boolean;
    readonly hasPopup?: 'menu' | 'dialog';
    readonly testId?: string;
    readonly onClick: () => void;
}

const BUBBLE_TONES: Record<ControlTone, string> = {
    default: 'bg-[var(--phq-call-surface-muted)] text-[var(--phq-call-ink)]',
    pressed: 'bg-[#FAFAFA] text-[#18181B]',
    brand: 'bg-[var(--phq-call-brand)] text-white',
    error: 'bg-[var(--phq-call-surface-muted)] text-[var(--phq-call-danger-soft)] shadow-[inset_0_0_0_2px_var(--phq-call-danger-soft)]',
    warning: 'bg-[var(--phq-call-surface-muted)] text-[var(--phq-call-warning-ink)] shadow-[inset_0_0_0_2px_var(--phq-call-warning-ink)]',
    unavailable: 'bg-[var(--phq-call-surface)] text-[var(--phq-call-ink-subtle)] shadow-[inset_0_0_0_1px_var(--phq-call-line-strong)]',
};

const CAPTION_TONES: Record<ControlTone, string> = {
    default: 'font-medium text-[var(--phq-call-ink-muted)]',
    pressed: 'font-medium text-[var(--phq-call-ink-muted)]',
    brand: 'font-medium text-[var(--phq-call-ink-muted)]',
    error: 'font-semibold text-[var(--phq-call-danger-soft)]',
    warning: 'font-semibold text-[var(--phq-call-warning-ink)]',
    unavailable: 'font-medium text-[var(--phq-call-ink-subtle)]',
};

const Badge = ({value}: {value: number}) => (
    <span className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[9px] bg-[var(--phq-call-brand)] px-1 text-[11px] font-semibold text-white">
        {value}
    </span>
);

const AlertMark = ({tone}: {tone: ControlTone}) => (
    <span
        aria-hidden="true"
        className={`absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-[10px] text-xs font-bold text-[var(--phq-call-canvas)] ${tone === 'warning' ? 'bg-[var(--phq-call-warning-ink)]' : 'bg-[var(--phq-call-danger-soft)]'}`}
    >
        !
    </span>
);

export const BubbleControl = forwardRef<HTMLButtonElement, ControlButtonProps>(
    ({icon, label, tone = 'default', badge, alert, disabled, pressed, expanded, hasPopup, testId, onClick}, ref) => (
        <button
            ref={ref}
            type="button"
            aria-label={label}
            title={label}
            aria-pressed={pressed}
            aria-expanded={expanded}
            aria-haspopup={hasPopup}
            data-testid={testId}
            disabled={disabled}
            onClick={onClick}
            className={`phq-call-press relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] disabled:cursor-not-allowed disabled:opacity-40 ${BUBBLE_TONES[tone]}`}
        >
            {icon}
            {badge !== undefined && badge > 0 && <Badge value={badge}/>}
            {alert && <AlertMark tone={tone}/>}
        </button>
    ),
);

BubbleControl.displayName = 'BubbleControl';

export const StageControl = forwardRef<HTMLButtonElement, ControlButtonProps>(
    ({icon, label, caption, tone = 'default', badge, alert, disabled, pressed, expanded, hasPopup, testId, onClick}, ref) => (
        <div className="flex w-20 flex-col items-center gap-2">
            <button
                ref={ref}
                type="button"
                aria-label={label}
                title={label}
                aria-pressed={pressed}
                aria-expanded={expanded}
                aria-haspopup={hasPopup}
                data-testid={testId}
                disabled={disabled}
                onClick={onClick}
                className={`phq-call-press relative inline-flex h-16 w-16 items-center justify-center rounded-[20px] disabled:cursor-not-allowed disabled:opacity-40 ${BUBBLE_TONES[tone]}`}
            >
                {icon}
                {badge !== undefined && badge > 0 && (
                    <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-[10px] bg-[var(--phq-call-brand)] px-1 text-[11px] font-semibold text-white">
                        {badge}
                    </span>
                )}
                {alert && <AlertMark tone={tone}/>}
            </button>
            {caption && <span aria-hidden="true" className={`max-w-full truncate text-[13px] ${CAPTION_TONES[tone]}`}>{caption}</span>}
        </div>
    ),
);

StageControl.displayName = 'StageControl';

export const BubbleEndButton = ({label, testId, onClick}: {label: string; testId?: string; onClick: () => void}) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="phq-call-press inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[var(--phq-call-danger)] px-3 text-[15px] font-semibold text-white"
    >
        <PhoneOff size={18} aria-hidden="true"/>
        {label}
    </button>
);

export const StageEndButton = ({label, testId, onClick}: {label: string; testId?: string; onClick: () => void}) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="phq-call-press inline-flex h-14 w-[200px] items-center justify-center gap-2 rounded-[20px] bg-[var(--phq-call-danger)] text-base font-semibold text-white"
    >
        <PhoneOff size={22} aria-hidden="true"/>
        {label}
    </button>
);

export const BubbleChip = ({icon, children, testId, status}: {icon: ReactNode; children: ReactNode; testId?: string; status?: string}) => (
    <span
        data-testid={testId}
        data-status={status}
        className="inline-flex h-6 min-w-0 items-center gap-1.5 rounded-lg bg-[var(--phq-call-surface)] px-2 text-xs font-semibold text-[var(--phq-call-ink-muted)]"
    >
        <span className="shrink-0">{icon}</span>
        <span className="truncate tabular-nums">{children}</span>
    </span>
);

export const StageChip = ({icon, children, testId, status}: {icon: ReactNode; children: ReactNode; testId?: string; status?: string}) => (
    <span
        data-testid={testId}
        data-status={status}
        className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[var(--phq-call-surface)] px-3 text-[13px] font-semibold text-[var(--phq-call-ink-muted)]"
    >
        {icon}
        <span className="tabular-nums">{children}</span>
    </span>
);

export type StatusTone = 'muted' | 'weak' | 'reconnecting' | 'sharing';

const NOTE_TONES: Record<StatusTone, string> = {
    muted: 'bg-[var(--phq-call-danger-note)] text-[var(--phq-call-danger-ink)]',
    weak: 'bg-[var(--phq-call-warning-note)] text-[var(--phq-call-warning-ink)]',
    reconnecting: 'bg-[var(--phq-call-surface)] text-[var(--phq-call-ink-secondary)]',
    sharing: 'bg-[var(--phq-call-sharing-bg)] text-[var(--phq-call-sharing-ink)]',
};

const PILL_TONES: Record<StatusTone, string> = {
    muted: 'bg-[var(--phq-call-surface-muted)] text-[var(--phq-call-ink)]',
    weak: 'bg-[var(--phq-call-warning-pill)] text-[var(--phq-call-warning-ink)]',
    reconnecting: 'bg-[var(--phq-call-surface)] text-[var(--phq-call-ink)]',
    sharing: 'bg-[var(--phq-call-sharing-bg)] text-[var(--phq-call-sharing-ink)]',
};

export const StatusNote = ({tone, icon, children}: {tone: StatusTone; icon: ReactNode; children: ReactNode}) => (
    <div role="status" className={`phq-call-swap flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium ${NOTE_TONES[tone]}`}>
        <span className="shrink-0">{icon}</span>
        <span className="min-w-0">{children}</span>
    </div>
);

export const StatusPill = ({tone, icon, children, testId}: {tone: StatusTone; icon: ReactNode; children: ReactNode; testId?: string}) => (
    <span role="status" data-testid={testId} className={`inline-flex h-8 items-center gap-2 rounded-full px-3 text-sm font-semibold ${PILL_TONES[tone]}`}>
        {icon}
        {children}
    </span>
);

export const BubbleIconButton = ({label, icon, testId, onClick}: {label: string; icon: ReactNode; testId?: string; onClick: () => void}) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        data-testid={testId}
        onClick={onClick}
        className="phq-call-press inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--phq-call-surface)] text-[var(--phq-call-ink)]"
    >
        {icon}
    </button>
);

export const ExpandButton = ({label, onClick}: {label: string; onClick: () => void}) => (
    <BubbleIconButton label={label} icon={<Maximize2 size={16} aria-hidden="true"/>} testId="group-call-expand" onClick={onClick}/>
);

export const MinimizeButton = ({label, testId, onClick}: {label: string; testId?: string; onClick: () => void}) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="phq-call-press inline-flex h-9 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface)] px-3 text-sm font-semibold text-[var(--phq-call-ink)]"
    >
        <Minimize2 size={18} aria-hidden="true"/>
        {label}
    </button>
);

export const BubbleActionButton = ({
    tone,
    icon,
    label,
    testId,
    ariaLabel,
    fit = 'equal',
    onClick,
}: {
    tone: 'accept' | 'danger' | 'brand' | 'neutral';
    icon?: ReactNode;
    label: string;
    testId?: string;
    ariaLabel?: string;
    /** 'content' lets a longer label (Hold & accept) take the room a shorter neighbour does not need. */
    fit?: 'equal' | 'content';
    onClick: () => void;
}) => {
    const tones = {
        accept: 'bg-[var(--phq-call-accept)] text-white',
        danger: 'bg-[var(--phq-call-danger)] text-white',
        brand: 'bg-[var(--phq-call-brand)] text-white',
        neutral: 'bg-[var(--phq-call-surface-muted)] text-[var(--phq-call-ink)]',
    } as const;
    return (
        <button
            type="button"
            data-testid={testId}
            aria-label={ariaLabel}
            onClick={onClick}
            className={`phq-call-press inline-flex h-11 min-w-0 ${fit === 'equal' ? 'flex-1' : 'flex-auto'} items-center justify-center gap-2 whitespace-nowrap rounded-2xl px-3 text-[15px] font-semibold ${tones[tone]}`}
        >
            {icon && <span className="inline-flex shrink-0">{icon}</span>}
            <span className="min-w-0 truncate">{label}</span>
        </button>
    );
};
